import keycloak from '@/config/keycloak';
import { reportApiError } from '@/utils/errors';

export { isAbortError } from '@/utils/errors';

const backendUrl = process.env.REACT_APP_BACKEND_URL ?? '';

export class ApiError extends Error {
  status: number;
  payload?: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof Error &&
  error.name === 'ApiError' &&
  'status' in error &&
  typeof error.status === 'number';

export const apiRequest = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  try {
    const result = await request<T>(path, init);
    if (init.signal?.aborted) throw new DOMException('Запрос отменён.', 'AbortError');
    return result;
  } catch (reason) {
    if (init.signal?.aborted) throw new DOMException('Запрос отменён.', 'AbortError');
    const error =
      typeof reason === 'object' && reason !== null
        ? reason
        : new Error(typeof reason === 'string' ? reason : '');
    reportApiError(error);
    throw error;
  }
};

const createResponseError = async (response: Response) => {
  const payload = await response.json().catch(() => null);
  const message =
    payload && typeof payload === 'object' && 'message' in payload
      ? String(payload.message)
      : response.status >= 500
        ? 'Возникли технические проблемы. Попробуйте позже.'
        : 'Не удалось выполнить запрос.';
  return new ApiError(message, response.status, payload);
};

const request = async <T>(path: string, init: RequestInit): Promise<T> => {
  if (keycloak.authenticated && keycloak.isTokenExpired(30)) {
    await keycloak.updateToken(30);
  }
  const response = await fetch(`${backendUrl}${path.startsWith('/') ? path : `/${path}`}`, {
    ...init,
    headers: {
      ...(keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {}),
      ...(!(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw await createResponseError(response);
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  const result: unknown = await (contentType.includes('application/json')
    ? response.json()
    : response.blob());
  if (
    result &&
    typeof result === 'object' &&
    'statusCode' in result &&
    typeof result.statusCode === 'number' &&
    result.statusCode >= 400
  ) {
    throw new ApiError(
      'message' in result ? String(result.message) : 'Не удалось выполнить запрос.',
      result.statusCode,
      result,
    );
  }
  return result as T;
};

export const api = {
  get: <T>(path: string, init: RequestInit = {}) => apiRequest<T>(path, init),
  post: <T>(path: string, body?: unknown, init: RequestInit = {}) =>
    apiRequest<T>(path, {
      ...init,
      method: 'POST',
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown, init: RequestInit = {}) =>
    apiRequest<T>(path, {
      ...init,
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};
